import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/site/Reveal";
import { INDUSTRY_PAGES } from "@/content/landings";
import { INDUSTRY_ROWS } from "@/content/site";
import PersonalCTA from "@/components/home/PersonalCTA";
import Contact from "@/components/site/Contact";

const crumbs = [{ name: "Home", to: "/" }, { name: "Industries", to: "/industries" }];
const OTHER = INDUSTRY_ROWS.filter((r) => r.to === "/industries");

const IndustriesPage = () => (
  <div data-testid="industries-page">
    <Seo title="Industries: Food, Wholesale, Packaging & Product Businesses" description="We market food manufacturers, distributors, wholesalers, grocers, hospitality, packaging and equipment manufacturers, food safety labs and trades. Businesses with something real to show." schema={[breadcrumbSchema(crumbs)]} />
    <header className="pt-32 md:pt-44 pb-16 md:pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
        <Breadcrumbs items={crumbs} />
        <p className="mt-10 label-eyebrow text-[#38BDF8]">Industries</p>
        <h1 className="mt-6 font-display uppercase text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.9] text-[#2E1B16] max-w-5xl">
          Where there's something <span className="font-accent font-normal normal-case text-[#38BDF8]">real to show.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#2E1B16]/60 max-w-xl leading-relaxed">
          Food is where we started and where we're strongest. The same approach works for any business with a product, a process or an operation worth watching.
        </p>
      </motion.div>
    </header>

    <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
      <ol className="border-t border-[#2E1B16]/12" data-testid="industries-list">
        {INDUSTRY_PAGES.map((p, i) => (
          <Reveal key={p.path} delay={i * 0.04}>
            <li>
              <Link to={p.path} data-testid={`industries-link-${p.key}`} className="group grid md:grid-cols-[5rem_1fr_auto] gap-4 md:gap-10 items-start border-b border-[#2E1B16]/12 py-9 md:py-12 hover:pl-3 transition-all duration-500">
                <span className="text-sm font-semibold text-[#38BDF8]">0{i + 1}</span>
                <div>
                  <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-[#2E1B16] group-hover:text-[#38BDF8] transition-colors">{p.nav}</h2>
                  <p className="mt-3 text-lg text-[#2E1B16]/65 leading-relaxed max-w-2xl">{p.intro[0]}</p>
                </div>
                <ArrowUpRight size={28} className="text-[#2E1B16]/25 group-hover:text-[#38BDF8] -rotate-45 group-hover:rotate-0 transition-all duration-300 justify-self-end" />
              </Link>
            </li>
          </Reveal>
        ))}
      </ol>

      <Reveal>
        <div className="mt-20 grid lg:grid-cols-12 gap-8">
          <h2 className="lg:col-span-4 font-display uppercase text-2xl md:text-3xl tracking-tight text-[#2E1B16]">Also a good fit</h2>
          <ul className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-x-10" data-testid="industries-other">
            {OTHER.map((r) => (
              <li key={r.title} className="border-t border-[#2E1B16]/12 py-4">
                <p className="font-display text-xl tracking-tight text-[#2E1B16]">{r.title}</p>
                <p className="mt-1 text-[#2E1B16]/55">{r.line}</p>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </section>

    <PersonalCTA />
    <Contact pageLabel="Industries page" />
  </div>
);

export default IndustriesPage;
